import type { SubmissionRepository } from '../../application/SubmissionRepository'
import type { SubmissionDataSource, SubmissionRecord } from '../../domain/submission'
import { commercialSubmissionStage } from '../../domain/submissionStage'

const fixtureFeed: Omit<SubmissionRecord, 'stage'>[] = [
  {
    submissionId: 'sub-lead-0000-000000000001',
    source: 'LEAD',
    leadId: 'lead-0000-4000-8000-000000000001',
    leadVersion: 1,
    leadStatusV4: 'NEW',
    clientName: 'Marie Tremblay',
    clientPhone: '450-555-0142',
    serviceAddress: '1450, Rue des Pins',
    serviceCity: 'Saint-Jérôme',
    servicePostalCode: 'J7Z 0C6',
    createdAt: '2026-08-02T13:00:00Z',
    siteOrigin: 'groupereca.com',
  },
  {
    submissionId: 'sub-lead-0000-000000000002',
    source: 'LEAD',
    leadId: 'lead-0000-4000-8000-000000000002',
    leadVersion: 1,
    leadStatusV4: 'CONTACTED_NOT_INTERESTED',
    clientName: 'Luc Bergeron',
    clientPhone: '514-555-0188',
    serviceAddress: '82, Avenue Principale',
    serviceCity: 'Blainville',
    servicePostalCode: 'J7C 2K9',
    createdAt: '2026-08-03T09:15:00Z',
    siteOrigin: 'deneigementstjerome.com',
  },
  {
    submissionId: 'sub-contract-0000-000000000003',
    source: 'CONTRACT',
    leadId: 'lead-0000-4000-8000-000000000003',
    contractId: 'contract-0000-4000-8000-000000000003',
    contractNumber: 'CTR-2026-041',
    contractStatusV2: 'PENDING_CLIENT',
    serviceRequested: 'Déneigement résidentiel',
    contractStatus: 'PENDING',
    clientName: 'Sylvie Roy',
    clientEmail: 'sylvie.roy@exemple.test',
    serviceAddress: '12, Rue de la Gare',
    serviceCity: 'Mirabel',
    servicePostalCode: 'J7J 1V1',
    totalAmountCents: 84900,
    sentAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    createdAt: '2026-08-10T10:00:00Z',
  },
  {
    submissionId: 'sub-contract-0000-000000000004',
    source: 'CONTRACT',
    leadId: 'lead-0000-4000-8000-000000000004',
    contractId: 'contract-0000-4000-8000-000000000004',
    contractNumber: 'CTR-2026-039',
    contractStatusV2: 'VIEWED',
    contractStatus: 'PENDING',
    openedSinceLastSend: true,
    firstSendChannel: 'EMAIL',
    clientName: 'Groupe Bâtico',
    clientPhone: '450-555-0177',
    serviceAddress: '900, Boulevard Industriel',
    serviceCity: 'Saint-Eustache',
    servicePostalCode: 'J7R 4Z9',
    totalAmountCents: 219000,
    sentAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: '2026-08-05T08:00:00Z',
  },
  {
    submissionId: 'sub-contract-0000-000000000005',
    source: 'CONTRACT',
    leadId: 'lead-0000-4000-8000-000000000005',
    contractId: 'contract-0000-4000-8000-000000000005',
    contractNumber: 'CTR-2026-030',
    contractStatusV2: 'ACTIVE',
    contractStatus: 'ACTIVE',
    signedOn: '2026-07-22',
    clientName: 'Résidences du Parc',
    totalAmountCents: 315000,
    sentAt: '2026-07-20T10:00:00Z',
    createdAt: '2026-07-15T08:00:00Z',
  },
  {
    submissionId: 'sub-contract-0000-000000000006',
    source: 'CONTRACT',
    leadId: 'lead-0000-4000-8000-000000000006',
    contractId: 'contract-0000-4000-8000-000000000006',
    contractNumber: 'CTR-2026-029',
    contractStatusV2: 'SIGNED',
    contractStatus: 'PENDING',
    signedOn: '2026-08-03',
    clientName: 'Ébénisterie Lambert',
    totalAmountCents: 128000,
    sentAt: '2026-08-01T10:00:00Z',
    createdAt: '2026-07-30T08:00:00Z',
  },
  {
    // Demande dont le Contrat est encore préparé (brouillon, pas envoyé) —
    // reste visible en A_FAIRE, ne disparaît plus le temps de la préparation.
    submissionId: 'sub-contract-0000-000000000007',
    source: 'CONTRACT',
    leadId: 'lead-0000-4000-8000-000000000007',
    contractId: 'contract-0000-4000-8000-000000000007',
    contractNumber: 'CTR-2026-045',
    contractStatusV2: 'DRAFT',
    contractStatus: 'DRAFT',
    clientName: 'Atelier du Nord',
    createdAt: '2026-09-10T08:00:00Z',
  },
  {
    // Contrat créé directement sur une fiche Client, sans Demande d'origine
    // (`leadId` absent) — n'est pas une Demande, exclu du flux ci-dessous.
    submissionId: 'sub-contract-0000-000000000008',
    source: 'CONTRACT',
    contractId: 'contract-0000-4000-8000-000000000008',
    contractNumber: 'CTR-2026-028',
    contractStatusV2: 'DECLINED',
    contractStatus: 'CANCELLED',
    clientName: 'Dépanneur du Coin',
    totalAmountCents: 42000,
    sentAt: '2026-07-10T10:00:00Z',
    createdAt: '2026-07-08T08:00:00Z',
  },
]

export function createFixtureSubmissionRepository(
  source: SubmissionDataSource = 'fixtures',
): SubmissionRepository {
  return {
    listFeed() {
      const items = fixtureFeed
        // Un Contrat créé directement sur une fiche Client (sans Demande
        // d'origine) n'est pas une Demande — même filtre que la vue SQL
        // `commercial_submissions_feed` (`c.lead_id is not null`).
        .filter((row) => row.source !== 'CONTRACT' || Boolean(row.leadId))
        .map((row) => {
          const stage = commercialSubmissionStage({
            leadStatusV4: row.leadStatusV4 as never,
            contractStatusV2: row.contractStatusV2 as never,
            sentAt: row.sentAt ?? null,
          })
          return stage ? { ...row, stage } : null
        })
        .filter((row): row is SubmissionRecord => row !== null)
      return Promise.resolve({ items, meta: { source, retrievedAt: new Date().toISOString() } })
    },
    getPipelineSettings() {
      return Promise.resolve({ followUpDelayHours: 48 })
    },
    updatePipelineSettings(command) {
      return Promise.resolve(command)
    },
    recordCallOutcome(command) {
      return Promise.resolve({
        leadId: command.leadId,
        statusV4:
          command.outcome === 'OUT_OF_AREA' || command.outcome === 'INVALID_CONTACT'
            ? 'CONTACTED_NOT_INTERESTED'
            : command.outcome,
      })
    },
    declineProposal(command) {
      return Promise.resolve({ contractId: command.contractId, statusV2: 'DECLINED' })
    },
    // Le lead de démonstration 450-555-0142 a un contrat libre au même numéro (CTR-2026-099).
    findContractsByPhone(phones) {
      return Promise.resolve(
        phones.includes('4505550142')
          ? [
              {
                phoneDigits: '4505550142',
                contractId: 'f3000000-0000-4000-a000-000000000099',
                contractNumber: 'CTR-2026-099',
                status: 'PENDING_CLIENT',
              },
            ]
          : [],
      )
    },
    assignContractToLead(command) {
      return Promise.resolve({
        leadId: command.leadId,
        contractId: command.contractId,
        contractNumber: 'CTR-2026-099',
        leadConverted: true,
      })
    },
  }
}
